import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="text-2xl font-bold text-gray-900">Privacy Policy</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="prose prose-blue max-w-none">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy for East End Prep Sports</h1>
                <p className="text-gray-600">Last Updated: August 23, 2025</p>
              </div>

              <div className="space-y-8">
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
                  <p className="text-gray-700 leading-relaxed">
                    East End Prep Sports ("we," "our," or "us") operates <code className="bg-gray-100 px-2 py-1 rounded text-sm">https://eastendprepsports.com</code> 
                    ("Site"), providing coverage of elementary school sports programs. We value students' and parents' privacy, take data protection seriously, and comply with applicable laws, including the Children's Online Privacy Protection Act (COPPA).
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>
                  
                  <h3 className="text-lg font-medium text-gray-800 mb-2">a. Information You Provide</h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Contact forms or sign-ups:</strong> Names, email addresses, and parent/guardian contact if applicable.</li>
                    <li><strong>Photos or media submissions:</strong> Student or team images, scores, game footage, and associated metadata (names, school, age group).</li>
                  </ul>

                  <h3 className="text-lg font-medium text-gray-800 mb-2 mt-4">b. Automatically Collected Information</h3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li><strong>Standard analytics:</strong> IP addresses, browser details, device type, pages visited, and timestamps for site performance.</li>
                    <li><strong>Cookies and similar tech:</strong> To manage user sessions, preferences, and analytics tracking.</li>
                  </ul>

                  <h3 className="text-lg font-medium text-gray-800 mb-2 mt-4">c. Embedded Third-Party Content</h3>
                  <p className="text-gray-700">
                    Data captured via social media embeds (e.g., YouTube, Instagram) if you interact with them through the site.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Use of Information</h2>
                  <p className="text-gray-700 mb-3">We use data to:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Operate and improve the Site.</li>
                    <li>Respond to inquiries and manage submissions.</li>
                    <li>Share news, updates, and event information (only with opt-in consent).</li>
                    <li>Generate statistics and reports on site usage for internal purposes.</li>
                    <li>Fulfill legal obligations and protect your rights and ours.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Children's Privacy (COPPA Compliance)</h2>
                  <p className="text-gray-700 mb-3">As an elementary-school-focused site, we recognize extra responsibilities:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>We do not knowingly collect personal information (like full names or contact details) from children under 13 without verifiable parental consent.</li>
                    <li>If parental consent is obtained, submitted data is used solely to facilitate coverage (e.g., team photos or game highlights).</li>
                    <li>Any data we collect from children is deleted upon request from a parent or guardian.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Cookies & Tracking</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>We use cookies for basic site functions, preferences, and analytics.</li>
                    <li>You can manage or disable cookies through your browser settings—although this may affect site functionality.</li>
                    <li>Third-party analytics (e.g., Google Analytics) may use cookies—see their respective policies.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Information Sharing</h2>
                  <p className="text-gray-700 mb-3">We do not sell or rent personal information. Disclosure only occurs when:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Service providers (e.g., hosting, email platforms, analytics) need it to support operations.</li>
                    <li>Legal reasons demand it (e.g., compliance or safety concerns).</li>
                    <li>Parental consent has been granted for content (e.g., posted photos or videos).</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Your Rights & Choices</h2>
                  <p className="text-gray-700 mb-3">You may:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Access, correct, or request deletion of your (or your child's) personal information.</li>
                    <li>Opt-out of any communications.</li>
                    <li>Withdraw parental consent for content previously submitted.</li>
                    <li>Manage or disable cookies via browser controls.</li>
                  </ul>
                  <p className="text-gray-700 mt-3">To exercise these rights or with inquiries, contact us.</p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Data Security</h2>
                  <p className="text-gray-700">
                    We implement security measures—such as encryption and access controls—to protect your information. No method is completely fail-proof. Please use caution when submitting personal data.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Links to Other Websites</h2>
                  <p className="text-gray-700">
                    If we link to third-party sites (e.g., school district pages or social platforms), their privacy practices are independent. Please review their privacy policies separately.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Policy Updates</h2>
                  <p className="text-gray-700">
                    We may revise this policy at any time. Significant changes will be communicated via the Site or email. The "Last Updated" date reflects current revisions.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Governing Law</h2>
                  <p className="text-gray-700">
                    This policy is governed by applicable U.S. and Tennessee state law.
                  </p>
                </section>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;